import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChange,
  SimpleChanges,
} from '@angular/core';
import { OverlayService } from '@services/overlay.service';
import { FormControl } from '@angular/forms';
import { BudgetSnapshotType, EventType } from '@services/gql.service';
import { EventService } from '@services/event.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SnapshotService } from '../compare-dropdown/snapshot.service';
import { SnapshotModalComponent } from '../snapshot-modal/snapshot-modal.component';
import { BudgetStore } from '../state/budget.store';
import { Overlay } from '@angular/cdk/overlay';
import {
  BudgetCompareMenuSnapshotChangeFn,
  BudgetCompareMenuSnapshotDisabled,
  BudgetCompareMenuSnapshotFormControl,
  BudgetCompareMenuSnapshotList,
  BudgetCompareMenuSnapshotLoading,
  BudgetCompareMenuSnapshotRefreshFn,
  BudgetCompareMenuSnapshotShowDelete,
  BudgetCompareMenuSnapshotShowEdit,
  BudgetCompareMenuSnapshotValue,
  defaultSnapshotChangeFn,
  defaultSnapshotRefreshFn,
} from '@components/budget-compare-menu/budget-compare-menu.models';
import { switchMap } from 'rxjs/operators';
import { of } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'aux-compare-dropdown-trial-insights',
  templateUrl: './compare-dropdown.component.html',
  styles: [
    `
      ::ng-deep .ng-option-selected::after {
        display: none;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompareDropdownComponent implements OnInit, OnChanges {
  BUDGET_SNAPSHOT_USER_CREATED = BudgetSnapshotType.BUDGET_SNAPSHOT_USER_CREATED;

  @Input() snapshotFormControl: BudgetCompareMenuSnapshotFormControl;

  @Input() snapshotList: BudgetCompareMenuSnapshotList;

  @Input() snapshotValue: BudgetCompareMenuSnapshotValue;

  @Input() snapshotDisabled: BudgetCompareMenuSnapshotDisabled;

  @Input() snapshotShowEdit: BudgetCompareMenuSnapshotShowEdit;

  @Input() snapshotShowDelete: BudgetCompareMenuSnapshotShowDelete;

  @Input() snapshotLoading: BudgetCompareMenuSnapshotLoading;

  @Input() snapshotChangeFn: BudgetCompareMenuSnapshotChangeFn;

  @Input() snapshotRefreshFn: BudgetCompareMenuSnapshotRefreshFn;

  constructor(
    private snapshotService: SnapshotService,
    private overlayService: OverlayService,
    private eventService: EventService,
    private budgetStore: BudgetStore,
    public overlay: Overlay
  ) {
    this.snapshotFormControl = new FormControl<string>('');
    this.snapshotList = [];
    this.snapshotValue = '';
    this.snapshotDisabled = false;
    this.snapshotShowEdit = true;
    this.snapshotShowDelete = true;
    this.snapshotLoading = false;
    this.snapshotChangeFn = defaultSnapshotChangeFn;
    this.snapshotRefreshFn = defaultSnapshotRefreshFn;
  }

  ngOnInit(): void {
    this.eventService
      .select$(EventType.CREATE_TRIAL_BUDGET_SNAPSHOT)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.overlayService.showFieldTooltip(
          'compare-snapshot-dropdown',
          'Snapshot successfully created'
        );
        this.snapshotService.getSnapshotList().toPromise();
      });

    this.snapshotFormControl.setValue(this.snapshotFormControl.defaultValue);

    this.snapshotFormControl.valueChanges
      .pipe(
        untilDestroyed(this),
        switchMap((snapshotName) => {
          this.snapshotChangeFn(snapshotName || '');
          if (!snapshotName) {
            this.snapshotService.setOriginalBudgetData();
            return of();
          }

          return this.snapshotService.getBudgetSnapshots(snapshotName);
        })
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('snapshotList' in changes) {
      this.processSnapshotList(changes.snapshotList);
    }

    if ('snapshotLoading' in changes) {
      this.processSnapshotLoading(changes.snapshotLoading);
    }
  }

  processSnapshotList(change: SimpleChange): void {
    if ('currentValue' in change) {
      const options = change.currentValue as BudgetCompareMenuSnapshotList;

      const isValueExist = options.find(({ value }) => value === this.snapshotFormControl.value);

      if (!isValueExist) {
        this.snapshotFormControl.setValue(this.snapshotFormControl.defaultValue);
      }
    }
  }

  processSnapshotLoading(change: SimpleChange): void {
    if ('currentValue' in change) {
      const loading = change.currentValue as boolean;

      if (loading || this.snapshotDisabled) {
        this.snapshotFormControl.disable();
      } else {
        this.snapshotFormControl.enable();
      }
    }
  }

  groupByFn(option: {
    label: string;
    value: string;
    create_date: string;
    snapshot_type: string;
    id: string;
  }) {
    switch (option.snapshot_type) {
      case BudgetSnapshotType.BUDGET_SNAPSHOT_USER_CREATED:
        return 'MANUAL SNAPSHOTS';
      default:
        return 'AUTOMATIC SNAPSHOTS ';
    }
  }

  async editSnapshot(
    event: Event,
    snapshot: {
      label: string;
      value: string;
      create_date: string;
      snapshot_type: string;
      id: string;
    }
  ) {
    event.stopPropagation();
    const name = snapshot.label;
    const response = await this.overlayService
      .open({
        content: SnapshotModalComponent,
        data: {
          name,
        },
      })
      .afterClosed$.toPromise();
    if (response.data?.name) {
      await this.emitUpdatedValue(
        name,
        response.data.name,
        () => {
          return this.snapshotService.updateSnapshots(snapshot, response.data?.name);
        },
        () => this.snapshotFormControl.setValue(response.data.name)
      );
    }
  }

  private async emitUpdatedValue(
    value: string,
    newName: string | undefined,
    callback: () => Promise<void>,
    updateControlCallback?: VoidFunction
  ) {
    if (value === this.snapshotFormControl.value) {
      this.snapshotChangeFn(newName || '');
      this.budgetStore.setLoading(true);

      if (updateControlCallback) {
        updateControlCallback();
      }

      // await callback();
      this.snapshotRefreshFn();
    } else {
      await callback();
    }

    this.budgetStore.setLoading(false);
  }

  async deleteSnapshot(
    event: Event,
    snapshot: {
      label: string;
      value: string;
      create_date: string;
      snapshot_type: string;
      id: string;
    }
  ) {
    event.stopPropagation();

    const response = await this.overlayService
      .openConfirmDialog({
        header: 'Remove Plan',
        message: `Are you sure you want to remove ${snapshot.label} plan?`,
        okBtnText: 'Remove',
      })
      .afterClosed$.toPromise();

    if (response.data?.result) {
      await this.emitUpdatedValue(snapshot.label, undefined, () =>
        this.snapshotService.removeBudgetSnapshots(snapshot)
      );
    }
  }
}
